/**
 * รายการใบรับเข้า — `GET /v1/goods-receipts`
 *
 * สามสถานะที่ต้องแยกจากกันเสมอ: กำลังโหลด / โหลดสำเร็จแต่ไม่มีรายการ /
 * โหลดไม่สำเร็จ ตารางว่างที่ใช้แทนทั้งสองอย่างหลังคือหน้าจอที่โกหกผู้ใช้ — และ
 * ที่นี่มันโกหกเรื่องที่แพงเป็นพิเศษ: "ยังไม่มีของเข้า" กับ "อ่านไม่ได้ว่ามีอะไรเข้า"
 * นำไปสู่การตัดสินใจคนละอย่างที่หน้างาน
 *
 * ⚠️ `plant_code` ที่นี่เป็น **ตัวกรอง** ไม่ใช่ขอบเขต ต่างจากตอนบันทึก: การกรอง
 * ตาม plant scope ของผู้เรียกเกิดขึ้นที่เซิร์ฟเวอร์เสมออยู่แล้ว ตัวเลือก
 * "ทุกโรงงานในขอบเขตของคุณ" จึงมีอยู่จริงและแปลว่า **ไม่ส่ง** `plant_code` ไป —
 * พนักงานที่ถือสองโรงงานต้องดูรวมได้ ไม่ใช่ถูกบังคับให้เลือกทีละแห่ง
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { GoodsReceiptFailureNotice } from './GoodsReceiptFailureNotice';
import {
  qualityStatusLabel,
  qualityStatusTone,
  receiptDetailSearch,
} from './goodsReceiptView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { orDash } from '../products/productView';
import {
  fetchGoodsReceipts,
  type GoodsReceiptFailure,
} from '../../lib/goodsReceipts/client';
import type {
  GoodsReceipt,
  GoodsReceiptListFilters,
} from '../../lib/goodsReceipts/types';

export interface GoodsReceiptListViewProps {
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** โรงงานที่ `plant_scope` ของผู้ใช้พามาถึงจริง — ดู `GoodsReceiptScreen` */
  readonly plants: readonly string[];
  /** ผู้ใช้ถือ marker ข้ามโรงงาน (AUDITOR) จึงเลือก "ทุกโรงงานในขอบเขต" ได้ */
  readonly crossPlant: boolean;
  /** มาจาก flag `can_create` ที่เซิร์ฟเวอร์ส่งมาเท่านั้น */
  readonly canCreate: boolean;
  readonly createHref: string;
}

type ListState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly items: readonly GoodsReceipt[];
      readonly nextCursor: string | null;
      readonly loadingMore: boolean;
      /** ความล้มเหลวของ "โหลดเพิ่ม" ไม่ควรลบแถวที่โหลดมาแล้วทิ้ง */
      readonly loadMoreFailure: GoodsReceiptFailure | null;
    }
  | GoodsReceiptFailure;

/** ค่าใน `<select>` ที่แปลว่า "ไม่ส่ง `plant_code` ให้เซิร์ฟเวอร์กรองตาม scope เอง" */
const ALL_PLANTS_VALUE = '';

export function GoodsReceiptListView({
  apiBaseUrl,
  fetchImpl,
  plants,
  crossPlant,
  canCreate,
  createHref,
}: GoodsReceiptListViewProps) {
  const { pathname } = useLocation();

  const [plantCode, setPlantCode] = useState<string>(plants[0] ?? ALL_PLANTS_VALUE);
  const [productDraft, setProductDraft] = useState('');
  const [locationDraft, setLocationDraft] = useState('');
  const [filters, setFilters] = useState<GoodsReceiptListFilters>({});
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<ListState>({ status: 'loading' });

  // ไม่มีโรงงานให้เห็นเลย = คำขอที่รู้อยู่แล้วว่าจะไม่มีอะไรตอบกลับ จึงไม่ยิง
  const canQuery = plants.length > 0 || crossPlant;

  useEffect(() => {
    if (!canQuery) {
      return;
    }
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading' });

    void fetchGoodsReceipts(
      { ...filters, ...(plantCode === ALL_PLANTS_VALUE ? {} : { plant_code: plantCode }) },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setState(
        result.status === 'ready'
          ? {
              status: 'ready',
              items: result.page.items,
              nextCursor: result.page.next_cursor,
              loadingMore: false,
              loadMoreFailure: null,
            }
          : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [canQuery, plantCode, filters, apiBaseUrl, fetchImpl, reloadToken]);

  const applyFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFilters({ product_code: productDraft, storage_location_code: locationDraft });
  };

  const loadMore = async () => {
    if (state.status !== 'ready' || state.loadingMore) {
      return;
    }
    const loaded = state;
    const cursor = loaded.nextCursor;
    if (cursor === null) {
      return;
    }
    setState({ ...loaded, loadingMore: true, loadMoreFailure: null });

    const result = await fetchGoodsReceipts(
      {
        ...filters,
        ...(plantCode === ALL_PLANTS_VALUE ? {} : { plant_code: plantCode }),
        cursor,
      },
      { apiBaseUrl, fetchImpl },
    );
    if (result.status === 'cancelled') {
      return;
    }
    setState(
      result.status === 'ready'
        ? {
            status: 'ready',
            items: [...loaded.items, ...result.page.items],
            nextCursor: result.page.next_cursor,
            loadingMore: false,
            loadMoreFailure: null,
          }
        : { ...loaded, loadingMore: false, loadMoreFailure: result },
    );
  };

  return (
    <div className="cap-page" data-testid="goods-receipt-list">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            รับเข้าสต๊อก
            <InfoHint
              label="เกี่ยวกับรายการนี้"
              text="รายการถูกจำกัดด้วยโรงงานที่คุณมีสิทธิ์เห็นเสมอ (D-004) —
            ของที่รับเข้าใหม่ทุกใบยังอยู่ในสถานะกักรอตรวจและเบิกไปใช้ไม่ได้ (D-059)"
            />
          </h1>
        </div>
        <div className="cap-page__actions">
          {canCreate ? (
            <Link
              className="cap-button cap-button--primary"
              data-testid="goods-receipt-create-link"
              to={{ pathname, search: createHref }}
            >
              รับของเข้าสต๊อก
            </Link>
          ) : null}
        </div>
      </div>

      {!canQuery ? (
        <div className="cap-notice" data-testid="goods-receipt-no-plant" role="status">
          <StatusBadge tone="warning">ไม่มีโรงงานที่คุณเห็น</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ของคุณยังไม่ครอบคลุมโรงงานใดเลย</h2>
          <p className="cap-notice__text">
            การรับเข้าเป็นของโรงงานใดโรงงานหนึ่งเสมอ และ{' '}
            <code className="cap-table__code">plant_code</code> เป็นขอบเขตสิทธิ์ ไม่ใช่ตัวกรอง
            (D-004) — เมื่อยังไม่มีโรงงานให้เห็น หน้าจอนี้จึงไม่ยิงคำขอที่รู้อยู่แล้วว่าไม่สมบูรณ์
            ติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์โรงงาน
          </p>
        </div>
      ) : null}

      {canQuery ? (
        <form className="cap-filters" onSubmit={applyFilters} aria-label="ตัวกรองรายการใบรับเข้า">
          <label className="cap-field">
            <span className="cap-field__label">โรงงาน (plant_code)</span>
            <select
              className="cap-input"
              name="plant_code"
              data-testid="goods-receipt-plant-select"
              value={plantCode}
              onChange={(event) => {
                setPlantCode(event.target.value);
              }}
            >
              {crossPlant ? (
                <option value={ALL_PLANTS_VALUE}>ทุกโรงงานในขอบเขตของคุณ</option>
              ) : null}
              {plants.map((code) => (
                <option key={code} value={code}>
                  โรงงาน {code}
                </option>
              ))}
            </select>
            <InfoHint
              label="เกี่ยวกับตัวกรองโรงงาน"
              text="เป็นตัวกรอง ไม่ใช่ขอบเขต — เซิร์ฟเวอร์กรองตามโรงงานที่คุณมีสิทธิ์เห็นซ้ำอีกชั้นเสมอ"
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">รหัสสินค้า</span>
            <input
              className="cap-input"
              type="search"
              name="product_code"
              data-testid="goods-receipt-product-filter"
              value={productDraft}
              onChange={(event) => {
                setProductDraft(event.target.value);
              }}
              placeholder="เช่น RM-STEEL-8MM"
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">ที่เก็บ</span>
            <input
              className="cap-input"
              type="search"
              name="storage_location_code"
              data-testid="goods-receipt-location-filter"
              value={locationDraft}
              onChange={(event) => {
                setLocationDraft(event.target.value);
              }}
              placeholder="เช่น RM-01"
            />
          </label>
          <button type="submit" className="cap-button cap-button--ghost">
            กรองรายการ
          </button>
        </form>
      ) : null}

      {canQuery && state.status === 'loading' ? (
        <div className="cap-notice" data-testid="goods-receipt-list-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการใบรับเข้า…</p>
        </div>
      ) : null}

      {canQuery && state.status !== 'loading' && state.status !== 'ready' ? (
        <GoodsReceiptFailureNotice
          failure={state}
          context="list"
          plantCode={plantCode}
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {state.status === 'ready' && state.items.length === 0 ? (
        <div className="cap-notice" data-testid="goods-receipt-list-empty" role="status">
          <StatusBadge tone="neutral">ไม่มีรายการ</StatusBadge>
          <h2 className="cap-notice__title">ยังไม่มีการรับเข้าตามเงื่อนไขนี้</h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่ไม่มีใบรับเข้าที่ตรงเงื่อนไขและอยู่ในโรงงานที่คุณมีสิทธิ์เห็น —
            ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {state.status === 'ready' && state.items.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="รายการใบรับเข้า">
          <div className="cap-card__head">
            <h2 className="cap-card__title">รายการใบรับเข้า</h2>
            <span className="cap-card__hint">แสดง {state.items.length} รายการที่โหลดมาแล้ว</span>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table">
              <caption className="cap-visually-hidden">
                รายการใบรับเข้าที่คุณมีสิทธิ์เห็น พร้อมสินค้า ที่เก็บ จำนวน วันที่ที่มีผล และสถานะคุณภาพ
                ของ lot ที่ถูกสร้าง
              </caption>
              <thead>
                <tr>
                  <th scope="col">เลขที่ใบรับเข้า</th>
                  <th scope="col">สินค้า</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">ที่เก็บ</th>
                  <th scope="col">จำนวน</th>
                  <th scope="col">วันที่ที่มีผล</th>
                  <th scope="col">สถานะคุณภาพ</th>
                </tr>
              </thead>
              <tbody>
                {state.items.map((receipt) => (
                  <tr
                    key={receipt.receipt_id}
                    data-testid={`goods-receipt-row-${receipt.receipt_id}`}
                    data-quality-status={receipt.quality_status}
                  >
                    <th scope="row" className="cap-table__row-head">
                      <Link
                        className="cap-table__link"
                        to={{ pathname, search: receiptDetailSearch(receipt.receipt_id) }}
                      >
                        {receipt.receipt_id}
                      </Link>
                    </th>
                    <td className="cap-table__code">{receipt.product_code}</td>
                    <td className="cap-table__code">{receipt.plant_code}</td>
                    <td className="cap-table__code">{receipt.storage_location_code}</td>
                    {/* NUMERIC เดินทางเป็น string ตลอด — ไม่จัดรูปแบบซ้ำที่นี่ */}
                    <td className="cap-table__code">{receipt.quantity}</td>
                    <td className="cap-table__code">{orDash(receipt.posting_date)}</td>
                    <td>
                      <StatusBadge tone={qualityStatusTone(receipt.quality_status)}>
                        {qualityStatusLabel(receipt.quality_status)}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cap-card__foot">
            {state.nextCursor === null ? (
              <span data-testid="goods-receipt-list-complete">
                แสดงครบทุกรายการที่ตรงเงื่อนไขแล้ว
              </span>
            ) : (
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="goods-receipt-list-more"
                onClick={() => {
                  void loadMore();
                }}
                disabled={state.loadingMore}
              >
                {state.loadingMore ? 'กำลังโหลดเพิ่ม…' : 'โหลดรายการถัดไป'}
              </button>
            )}
          </div>
        </section>
      ) : null}

      {state.status === 'ready' && state.loadMoreFailure !== null ? (
        <GoodsReceiptFailureNotice
          failure={state.loadMoreFailure}
          context="list"
          plantCode={plantCode}
        />
      ) : null}
    </div>
  );
}
